import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../hooks/storeHooks';
import { unwrapResult } from '@reduxjs/toolkit';
import { REDUX_REQUEST_STATUS } from '../../types/commonTypes';
import {
  getWhatsAppGroupLink,
  getVendorEventGroupRecord,
} from '../../store/events/eventThunk';
import {
  getCorporateEventWhatsAppGroupRecord,
  getCorporateEventWhatsAppGroupLink,
} from '../../store/b2b/corporateEvent/corporateEventThunk';
import { IWhatsAppGroupInfo } from '../../types/eventTypes';
import { ICorporateEventMessage, ICorporateEventWhatsAppGroupInfo } from '../../interfaces/corporateEvent';
import { IEventDetailMessages } from '../../types/eventTypes';
import { EventTypeFilterEnum } from '../../constants/enums';

interface UseWhatsAppGroupReturn {
  whatsAppGroupDetails: IWhatsAppGroupInfo | ICorporateEventWhatsAppGroupInfo | null;
  whatsAppGroupLink: string;
  isWhatsAppModalOpen: boolean;
  isLoading: boolean;
  openWhatsAppModal: () => void;
  closeWhatsAppModal: () => void;
  handleWhatsAppClick: () => void;
}

export const useWhatsAppGroup = (chatMessage: IEventDetailMessages | ICorporateEventMessage, eventType: EventTypeFilterEnum): UseWhatsAppGroupReturn => {
  const dispatch = useAppDispatch();
  const [whatsAppGroupDetails, setWhatsAppGroupDetails] = useState<
    IWhatsAppGroupInfo | ICorporateEventWhatsAppGroupInfo | null
  >(null);
  const [whatsAppGroupLink, setWhatsAppGroupLink] = useState<string>('');
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchWhatsAppGroupInfo = useCallback(async () => {
    const b2bChat = chatMessage as ICorporateEventMessage;
    const b2cChat = chatMessage as IEventDetailMessages;
    if (!b2bChat?.corporateEventVendorId && !b2cChat?.vendorEventId) return;

    setIsLoading(true);
    try {
      if (eventType === EventTypeFilterEnum.B2B && b2bChat?.isWhatsappGroupCreated) {
        const details = await dispatch(
          getCorporateEventWhatsAppGroupRecord({ corporateEventVendorId: b2bChat.corporateEventVendorId })
        );
        if (details.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
          const unwrappedResult = unwrapResult(details);
          setWhatsAppGroupDetails(unwrappedResult);
        } else {
          setWhatsAppGroupDetails(null);
        }

        const groupLink = await dispatch(
          getCorporateEventWhatsAppGroupLink({ corporateEventVendorId: b2bChat.corporateEventVendorId })
        );
        if (groupLink.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
          const unwrappedResult = unwrapResult(groupLink).data.invitationLink;
          setWhatsAppGroupLink(unwrappedResult);
        } else {
          setWhatsAppGroupLink('');
        }
      } else {
        if (b2cChat?.isWhatsappGroupCreated) {
          // B2C/Prepaid
          const details = await dispatch(getVendorEventGroupRecord({ vendorEventId: b2cChat.vendorEventId }));
          if (details.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
            const unwrappedResult = unwrapResult(details);
            setWhatsAppGroupDetails(unwrappedResult);
          } else {
            setWhatsAppGroupDetails(null);
          }

          const groupLink = await dispatch(getWhatsAppGroupLink({ vendorEventId: b2cChat.vendorEventId }));
          if (groupLink.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
            const unwrappedResult = unwrapResult(groupLink).data.invitationLink;
            setWhatsAppGroupLink(unwrappedResult);
          } else {
            setWhatsAppGroupLink('');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching WhatsApp group info:', error);
      // Reset to null on error
      setWhatsAppGroupDetails(null);
      setWhatsAppGroupLink('');
    } finally {
      setIsLoading(false);
    }
  }, [chatMessage, eventType, dispatch]);

  // Reset state when chat changes
  useEffect(() => {
    // Reset state immediately when chat changes
    setWhatsAppGroupDetails(null);
    setWhatsAppGroupLink('');
    setIsWhatsAppModalOpen(false);

    // Then fetch new chat's WhatsApp group info
    if (chatMessage) {
      fetchWhatsAppGroupInfo();
    }
  }, [chatMessage]);

  const openWhatsAppModal = useCallback(() => {
    setIsWhatsAppModalOpen(true);
  }, []);

  const closeWhatsAppModal = useCallback(() => {
    setIsWhatsAppModalOpen(false);
  }, []);

  const handleWhatsAppClick = useCallback(async () => {
    if (!whatsAppGroupDetails || !('name' in whatsAppGroupDetails) || !whatsAppGroupDetails.name) {
      await fetchWhatsAppGroupInfo();
    }
    openWhatsAppModal();
  }, [whatsAppGroupDetails, fetchWhatsAppGroupInfo, openWhatsAppModal]);

  return {
    whatsAppGroupDetails,
    whatsAppGroupLink,
    isWhatsAppModalOpen,
    openWhatsAppModal,
    closeWhatsAppModal,
    handleWhatsAppClick,
    isLoading,
  };
};
